import {
  withAuthenticationRequired,
  WithAuthenticationRequiredOptions
} from '@auth0/auth0-react'
import type { NextPage } from 'next'
import { useRouter } from 'next/router'
import { FC, useEffect, useMemo } from 'react'

import { useAuth } from 'src/providers/AuthProvider'
import { useUser } from 'src/providers/UserProvider'

import { Redirecting } from 'src/components/layouts/Redirecting'

import { dashboardPath, homePath } from 'src/config/paths'

export const withAdminRequired = <P extends object>(Page: NextPage): FC<P> => {
  return function ProtectedPage() {
    const { isLoading, isStaff } = useAuth()
    const router = useRouter()

    useEffect(() => {
      if (!isLoading && !isStaff) {
        router.replace(homePath)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isStaff, isLoading])

    const AdminAuthedPage = withAuthenticationRequired(Page as NextPage, {
      onRedirecting: Redirecting
    })
    return <AdminAuthedPage />
  }
}
/**
 * Guards a page by checking roles. Checking for advisor role, also qualifying staff role
 * @param Page Page component to guard
 * @returns
 */
export const withAdvisorRequired = <P extends object>(
  Page: NextPage
): FC<P> => {
  return function ProtectedPage() {
    const { isLoading, isAdvisor, isStaff } = useAuth()
    const router = useRouter()

    useEffect(() => {
      if (!isLoading && !isAdvisor && !isStaff) {
        router.replace(homePath)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAdvisor, isStaff, isLoading])

    const AdminAuthedPage = withAuthenticationRequired(Page as NextPage, {
      onRedirecting: Redirecting
    })
    return <AdminAuthedPage />
  }
}

export const withAuthRequired = <P extends object>(
  Page: NextPage,
  // Callers can override Auth0 options — e.g. a `returnTo` that preserves the
  // query string (the default returnTo is pathname-only).
  options?: Omit<WithAuthenticationRequiredOptions, 'onRedirecting'>
): FC<P> => {
  return withAuthenticationRequired(Page as NextPage, {
    onRedirecting: Redirecting,
    ...options
  })
}

export const withAccountRequired = <P extends object>(
  Page: NextPage
): FC<P> => {
  return function AccountRequiredPage() {
    const { user, isLoading } = useUser()
    const router = useRouter()

    const requiresAccount = useMemo(() => {
      return !isLoading && user && !user?.Account
    }, [user, isLoading])

    useEffect(() => {
      if (requiresAccount) {
        router.replace({
          pathname: dashboardPath(),
          query: {
            returnTo: router.asPath
          }
        })
      }
    }, [requiresAccount, router])

    if (isLoading || requiresAccount) return null

    return <Page />
  }
}
